// 🧹 Imports (same as before)
import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Stack,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  Badge,
  ListItemText,
  ListItemIcon,
  Divider,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { auth, db } from "../../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";

// 🧠 Component
export default function AccountantAppBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = auth.currentUser;

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [notiAnchor, setNotiAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);

  const openMenu = Boolean(menuAnchor);
  const openNoti = Boolean(notiAnchor);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const avatarUrl = `https://api.dicebear.com/7.x/identicon/svg?seed=${
    user?.uid || "default"
  }`;
  const showBack = location.pathname.includes("/client/");
  const pageTitle = showBack ? "Client Profile" : "";

  const navItems = [
    // { label: "Reports", path: "/accountant/reports" },
    // { label: "Invite", path: "/accountant/invite" },
  ];

  const handleLogout = async () => {
    await auth.signOut();
    navigate("/login");
  };

  // 🔔 Fetch notifications
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, "accountantNotifications"),
      where("accountantUid", "==", user.uid),
      orderBy("timestamp", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setNotifications(data.slice(0, 10));
    });
    return () => unsub();
  }, [user?.uid]);

  const handleNotificationClick = async (notif) => {
    setNotiAnchor(null);
    await updateDoc(doc(db, "accountantNotifications", notif.id), {
      read: true,
    });
    if (notif.clientEmail && notif.receiptId) {
      navigate(
        `/accountant/client/${notif.clientEmail}?receipt=${notif.receiptId}`
      );
    }
  };

  // 🧹 Mark All Read
  const handleMarkAllRead = async () => {
    const batch = writeBatch(db);
    notifications
      .filter((n) => !n.read)
      .forEach((n) => {
        const ref = doc(db, "accountantNotifications", n.id);
        batch.update(ref, { read: true });
      });
    await batch.commit();
  };

  return (
    <AppBar position="static" color="default" elevation={1}>
      <Toolbar sx={{ justifyContent: "space-between" }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          {showBack && (
            <Tooltip title="Back">
              <IconButton onClick={() => navigate(-1)}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
          )}
          <Typography variant="h6" fontWeight="bold">
            SortlyBooks
          </Typography>
          {pageTitle && (
            <Typography variant="body1" sx={{ ml: 1 }} color="text.secondary">
              / {pageTitle}
            </Typography>
          )}
          {navItems.map((item) => (
            <Button
              key={item.label}
              color={
                location.pathname.startsWith(item.path) ? "primary" : "inherit"
              }
              onClick={() => navigate(item.path)}
            >
              {item.label}
            </Button>
          ))}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Notifications">
            <IconButton onClick={(e) => setNotiAnchor(e.currentTarget)}>
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsNoneIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title={user?.email || "Account"}>
            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <Avatar src={avatarUrl} sx={{ width: 32, height: 32 }} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Menu
          anchorEl={menuAnchor}
          open={openMenu}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem>
            <Button
              variant="outlined"
              onClick={() => (window.location.href = "/api/quickbooks/auth")}
              sx={{ ml: 2 }}
            >
              Connect QuickBooks
            </Button>
          </MenuItem>
          <MenuItem disabled>{user?.email}</MenuItem>
          <MenuItem onClick={handleLogout}>Logout</MenuItem>
        </Menu>

        <Menu
          anchorEl={notiAnchor}
          open={openNoti}
          onClose={() => setNotiAnchor(null)}
          PaperProps={{ sx: { width: 320 } }}
        >
          {notifications.length === 0 ? (
            <MenuItem disabled>No notifications</MenuItem>
          ) : (
            <>
              {unreadCount > 0 && (
                <MenuItem
                  onClick={handleMarkAllRead}
                  sx={{ fontWeight: "bold", color: "primary.main" }}
                >
                  🧹 Mark all as read
                </MenuItem>
              )}
              <Divider />
              {notifications.map((n, i) => (
                <MenuItem
                  key={i}
                  onClick={() => handleNotificationClick(n)}
                  sx={{
                    backgroundColor: n.read ? "transparent" : "#f0f9ff",
                    whiteSpace: "normal",
                  }}
                >
                  <ListItemIcon>
                    <NotificationsActiveIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{n.message}</ListItemText>
                </MenuItem>
              ))}
            </>
          )}
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
